import {
    faAngleDoubleLeft,
    faAngleDoubleRight,
    faChevronLeft,
    faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { ReactNode } from 'react';
import styled from 'styled-components';

interface StyledButtonGroupProps {
    isDisabled?: boolean;
    justifyContent?: string;
    children: ReactNode;
}
const StyledButtonGroup = styled.div<StyledButtonGroupProps>`
    display: inline-flex;
    justify-content: ${({ justifyContent }) => justifyContent || 'center'};
    ${({ isDisabled }) =>
        isDisabled &&
        `
    pointer-events: none;
    opacity: 0.6;
  `}

    /* Remove extra border between buttons */
  & > button:not(:last-child) {
        border-right: none;
    }
`;

interface StyledButtonProps {
    isActive?: boolean;
    disabled?: boolean;
    children: ReactNode;
}
const StyledButton = styled.button<StyledButtonProps>`
    padding: 10px 16px;
    font-size: 14px;
    border: 1px solid ${({ theme }) => theme.gray7};
    background: ${({ isActive, theme }) => (isActive ? theme.gray10 : '#FFFFFF')};
    color: ${({ theme }) => theme.gray2};
    cursor: pointer;
    outline: none;
    margin: 0;

    &:disabled {
        cursor: not-allowed;
        color: ${({ theme }) => theme.gray7};
    }

    &:active {
        background: ${({ theme }) => theme.gray9};
        border-color: ${({ theme }) => theme.gray6};
    }

    ,
    &:hover {
        background: ${({ isActive, theme }) => (isActive ? theme.gray10 : '#FFFFFF')};
        border-color: ${({ theme }) => theme.gray6};
    }

    &:not([disabled]):focus,
    &:focus {
        border-color: ${({ theme }) => theme.gray6};
        outline: none;
    }

    &:first-child {
        border-radius: 8px 0px 0px 8px;
    }
    &:last-child {
        border-radius: 0px 8px 8px 0px;
    }
`;

interface IPaginationButtonProps
    extends Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'children' | 'aria-label'> {
    isActive?: boolean;
    isDisabled?: boolean;
    'data-testid'?: string;
}

const PaginationButton = ({
    children,
    'data-testid': dataTestId,
    isActive,
    isDisabled,
    ...rest
}: IPaginationButtonProps) => {
    return (
        <StyledButton data-testid={dataTestId} isActive={isActive} disabled={isDisabled} {...rest}>
            {children}
        </StyledButton>
    );
};

/* Pagination UI Component */

interface IPaginationUIProps {
    pages: number[];
    prevText?: string;
    nextText?: string;
    isPrevDisabled?: boolean;
    isNextDisabled?: boolean;
    disablePagination?: boolean;
    onPrev: () => void;
    onNext: () => void;
    isActive: (page: number) => boolean;
    onPageChange: (page: number) => void;
    goToFirstPage?: () => void;
    goToLastPage?: () => void;
    isFirstAndLastNavigation?: boolean;
}

export const PaginationUI = ({
    pages,
    prevText,
    nextText,
    isPrevDisabled,
    isNextDisabled,
    disablePagination,
    onPrev,
    onNext,
    isActive,
    onPageChange,
    goToFirstPage,
    goToLastPage,
    isFirstAndLastNavigation = false,
}: IPaginationUIProps) => {
    return (
        <StyledButtonGroup isDisabled={disablePagination} justifyContent="center">
            {isFirstAndLastNavigation && goToFirstPage && (
                <PaginationButton
                    onClick={goToFirstPage}
                    aria-label="First page"
                    isDisabled={isPrevDisabled}
                    data-testid="first-page-button"
                >
                    <FontAwesomeIcon icon={faAngleDoubleLeft} />
                </PaginationButton>
            )}
            <PaginationButton
                onClick={onPrev}
                aria-label="Previous page"
                isDisabled={isPrevDisabled}
                data-testid="previous-button"
            >
                {prevText ? prevText : <FontAwesomeIcon icon={faChevronLeft} />}
            </PaginationButton>
            {pages.map(page => (
                <PaginationButton
                    key={`page_btn_${page}`}
                    isActive={isActive(page)}
                    onClick={() => onPageChange(page)}
                    data-testid={`pagination_button${page}`}
                >
                    {page}
                </PaginationButton>
            ))}
            <PaginationButton
                aria-label="Next page"
                data-testid="next-button"
                onClick={onNext}
                isDisabled={isNextDisabled}
            >
                {nextText ? nextText : <FontAwesomeIcon icon={faChevronRight} />}
            </PaginationButton>
            {isFirstAndLastNavigation && goToLastPage && (
                <PaginationButton
                    onClick={goToLastPage}
                    isDisabled={isNextDisabled}
                    aria-label="Last page"
                    data-testid="last-page-button"
                >
                    <FontAwesomeIcon icon={faAngleDoubleRight} />
                </PaginationButton>
            )}
        </StyledButtonGroup>
    );
};
